import { useState, type ReactNode } from 'react'

import { validationMessages, type RegistryKind } from '@/api/actions'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from '@/components/ui/toast'

import { useResetDoc } from '../hooks'

/**
 * Where a registry document lives, in the words the page uses: `builtin` (the studio's own,
 * untouched), `changed` (the parent's copy of a built-in — "Reset to original" brings the built-in
 * back), `own` (made by the parent or the AI — can be deleted).
 */
export type RegistryPlace = 'builtin' | 'changed' | 'own'

export function registryPlace(doc: { origin?: unknown; source?: unknown }): RegistryPlace {
  if (doc.source === 'override') return 'changed'
  if (doc.source === 'seed') return 'builtin'
  if (doc.source === 'data') return 'own'
  return doc.origin === 'builtin' ? 'builtin' : 'own'
}

/** "Reset to original" with its confirmation: `open()` asks, `dialog` must be rendered. */
export function useResetFlow(kind: RegistryKind, id: string, title: string, onDone?: () => void): { open(): void; dialog: ReactNode; pending: boolean } {
  const [open, setOpen] = useState(false)
  const reset = useResetDoc(kind)
  const noun = kind === 'rule' ? 'world rule' : 'action'
  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Go back to the original “{title}”?</DialogTitle>
          <DialogDescription>Your changes to this built-in {noun} are thrown away and the studio’s own version comes back.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={reset.isPending}
            onClick={() =>
              reset.mutate(id, {
                onSuccess: () => {
                  setOpen(false)
                  toast.success('Back to the original')
                  onDone?.()
                },
                onError: (err) => toast.error('Could not reset it', { description: validationMessages(err).join(' ') }),
              })
            }
          >
            Reset to original
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
  return { open: () => setOpen(true), dialog, pending: reset.isPending }
}
